import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation } from "@apollo/client/react";

import { viewVisual } from "#app/components/view-options/view-type-meta";
import { RecordEntityViewVisitDocument, type EntityViewRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { AnalyticsContext, DashboardEvent, useAnalytics } from "#app/lib/analytics";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import {
  dashboardRefKey,
  resolveStoredDashboard,
  useDashboardSurfaces,
  type ResolvedDashboardRef,
  type StoredDashboardRef,
} from "./use-dashboard-surfaces";
import { RECENTS_SECTION_ROWS } from "#app/lib/constants/recents";


/**
 * The viewer's recently-opened dashboards, mirrored per workspace in
 * localStorage (the nav-prefs first-paint-mirror pattern: the list must be
 * readable synchronously, because the module menu decides whether the Recent
 * section exists at all before any query resolves).
 *
 * A visit stores the dashboard's OWN identity snapshot (name/icon/color of
 * the view, not its host entity); the live view row always wins once it
 * resolves, so a rename or recolor reaches the menu on its next mount.
 *
 * This is the shelf ORDER only. The time of each visit is the server's
 * `EntityView.viewerLastViewedAt`, which `useRecordDashboardVisit` writes.
 */
export function useRecentDashboards() {
  const { activeWorkspace } = useWorkspace();
  const { surfaceByRefKey, isLoaded } = useDashboardSurfaces();
  const { state, update } = usePersistedState(
    `dashboard-recents:${activeWorkspace?.workspaceId ?? ""}`,
    { visits: [] as StoredDashboardRef[] },
  );

  const recents = useMemo<ResolvedDashboardRef[]>(() => {
    return state.visits
      .map((visit) => resolveStoredDashboard(visit, surfaceByRefKey, isLoaded))
      .filter((resolved) => resolved !== null);
  }, [state.visits, surfaceByRefKey, isLoaded]);

  // The functional patch reads the CURRENT store, so a click-record and the
  // page's mount-record in one gesture never drop each other's write.
  const recordVisit = useCallback(
    (visit: StoredDashboardRef) => {
      const key = dashboardRefKey(visit);
      update((current) => ({
        visits: [
          visit,
          ...current.visits.filter((v) => dashboardRefKey(v) !== key),
        ].slice(0, RECENTS_SECTION_ROWS),
      }));
    },
    [update],
  );

  return { recents, recordVisit };
}

/**
 * Opening a dashboard is a visit, whichever way the viewer got there — a
 * directory row, a tab strip, a bookmark. Each dashboard route calls this, so
 * the "Date viewed" column does not depend on the entry point. It records once
 * per view, not per re-render of the row (a rename lands a new row object).
 *
 * Two writes: the server stamps `viewerLastViewedAt` (the mutation answers
 * with the row, so every cached list shows the new date), and the local shelf
 * moves the dashboard to the top. The stored `viewKey` follows the same rule
 * as `useDashboardSurfaces`, or the shelf would never resolve the entry: a
 * workflow view by its built-in key, a workspace view by its id.
 */
export function useRecordDashboardVisit(
  kind: StoredDashboardRef["kind"],
  scopeId: string | null | undefined,
  view: EntityViewRow | null,
) {
  const { recordVisit } = useRecentDashboards();
  const [recordServerVisit] = useMutation(RecordEntityViewVisitDocument);
  const { track } = useAnalytics();
  const recordedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!scopeId || !view) return;
    // A list or board view on the same route ends the visit, so a return to
    // the dashboard counts again.
    if (view.type !== "dashboard") {
      recordedRef.current = null;
      return;
    }
    if (recordedRef.current === view.id) return;
    recordedRef.current = view.id;
    const viewKey = kind === "workflow" ? (view.builtInKey ?? view.id) : view.id;
    recordVisit({ kind, id: scopeId, viewKey, name: view.name, ...viewVisual(view) });
    // A lost visit only leaves an older date; it is not worth a toast.
    recordServerVisit({ variables: { id: view.id } }).catch(() => {});
    // The route map cannot split a custom view's type; the visit can.
    track({
      context: AnalyticsContext.Dashboard,
      event: DashboardEvent.Open,
      data: { scope: kind, builtIn: view.builtInKey != null, visibility: view.visibility },
    });
  }, [kind, scopeId, view, recordVisit, recordServerVisit, track]);
}
